"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { Check } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z, ZodString } from "zod";

import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { MarkdownText } from "@/components/ui/markdown-text";
import { selectGroupLabelClassName } from "@/components/ui/select-input";
import { ContactPrefillTool } from "@/components/webmcp/contact-prefill-tool";
import {
  appendAppLinkAdClickIdsToUrl,
  getAdClickIdsFromUrl,
  resolveAppLinkAdClickIds,
} from "@/lib/ad-click-ids";
import rootLogger from "@/lib/logging";
import { snakeCaseObjectKeys } from "@/lib/transforms";
import { cn } from "@/lib/utils";
import {
  readContactPrefill,
  subscribeContactPrefill,
} from "@/lib/webmcp/contact-prefill";
import { FormContext, formSchema } from "@/schemas";
import { formFieldsSchema } from "@/schemas/forms";

import { Spinner } from "../spinner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { labelVariants } from "../ui/label";
import { Link } from "../ui/link";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { TypographyP } from "../ui/typography";
import {
  buildIneligibleRedirectUrl,
  findEligibilityGate,
  isIneligible,
} from "./eligibility-gate";
import { EligibilityNote } from "./eligibility-note";
import { useHubspotContext } from "./hubspot_context";
import { COUNTRY_FIELD_NAME, localizeRevenueLabel } from "./revenue-currency";
import { applySubmitValues } from "./submit-values";
import { calculateFormHeight } from "./utils";

const logger = rootLogger.child({ component: "form-builder" });

/**
 * Maps HubSpot field names and input types to standard HTML autocomplete tokens.
 * @see https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill
 */
const FIELD_NAME_TO_AUTOCOMPLETE: Record<string, string> = {
  email: "email",
  firstname: "given-name",
  first_name: "given-name",
  lastname: "family-name",
  last_name: "family-name",
  phone: "tel",
  mobilephone: "tel",
  company: "organization",
  domain: "url",
  jobtitle: "organization-title",
  job_title: "organization-title",
  address: "street-address",
  city: "address-level2",
  state: "address-level1",
  zip: "postal-code",
  postal_code: "postal-code",
  country: "country-name",
};

const INPUT_TYPE_TO_AUTOCOMPLETE: Record<string, string> = {
  email: "email",
  tel: "tel",
  url: "url",
};

/** Revenue field that needs locale-aware currency display */
const REVENUE_FIELD_NAME = "0-2/ib_monthly_revenue_usd_reported";

function getAutocomplete(
  fieldName: string,
  inputType?: string
): string | undefined {
  // Strip HubSpot company property prefix (e.g. "0-2/domain" → "domain")
  const normalizedName = fieldName.replace(/^\d+-\d+\//, "").toLowerCase();
  return (
    FIELD_NAME_TO_AUTOCOMPLETE[normalizedName] ??
    (inputType ? INPUT_TYPE_TO_AUTOCOMPLETE[inputType] : undefined)
  );
}
/*
This regular expression matches various phone number formats:

- ^([+]?[\s0-9]+)?: Matches an optional '+' sign followed by spaces and/or digits (for country code).
- (\d{3}|[(]?[0-9]+[)])?: Matches three digits or any digits inside optional parentheses (for area code).
- ([-]?[\s]?[0-9])+: Matches a digit preceded by an optional dash or space, repeating one or more times (for the local number).
- $: Matches the end of the string.

This regex matches phone numbers with/without country codes, parentheses around the area code,
and spaces or dashes between number groups. As it's permissive it can match invalid numbers.
*/
const phoneRegex = new RegExp(
  /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/
);

const submitFormToHubspot = (
  guid: string,
  formName: string,
  data: FormData,
  hubspotContext: FormContext,
  headers?: Record<string, string>
) => {
  return axios({
    url: "/api/submit-hubspot-form",
    method: "POST",
    data: {
      guid,
      formName,
      data,
      hubspotContext,
    },
    headers,
  });
};

const _fieldDefinitionTypes = z.discriminatedUnion("_type", [
  ...formFieldsSchema,
]);
type FieldDefinitionTypes = z.infer<typeof _fieldDefinitionTypes>;
/** Everything except the display-only eligibility note. */
type InputFieldDefinition = Exclude<
  FieldDefinitionTypes,
  { _type: "eligibilityNote" }
>;

interface ValidationMessages {
  required: string;
  invalidEmail: string;
  invalidNumber: string;
  invalidPhone: string;
}

function convertFieldDefinitionToResolver(
  field: InputFieldDefinition,
  validationMessages: ValidationMessages
) {
  let schema: [string, z.ZodType];
  switch (field._type) {
    case "textField":
      if (field.type === "email") {
        schema = [
          field.name,
          z.string().email({ message: validationMessages.invalidEmail }),
        ];
      } else if (field.type === "number") {
        schema = [
          field.name,
          z.string().regex(/^\d+$/, {
            message: validationMessages.invalidNumber,
          }),
        ];
      } else if (field.type === "tel") {
        schema = [
          field.name,
          z.string().regex(phoneRegex, {
            message: validationMessages.invalidPhone,
          }),
        ];
      } else if (field.type === "url") {
        schema = [field.name, z.string().url()];
      } else {
        schema = [field.name, z.string()];
      }
      if (!field.required) {
        schema = [schema[0], z.union([schema[1].nullish(), z.literal("")])];
      } else {
        schema = [
          schema[0],
          (schema[1] as ZodString).min(1, {
            message: validationMessages.required,
          }),
        ];
      }
      break;
    case "checkbox":
      // Required checkbox must be checked (true); optional accepts boolean
      schema = [
        field.name,
        field.required ? z.literal(true) : z.boolean().optional(),
      ];
      break;
    default:
      schema = [field.name, z.string()];
      if (!field.required) {
        schema = [schema[0], schema[1].nullish()];
      } else {
        schema = [
          schema[0],
          (schema[1] as ZodString).min(1, {
            message: validationMessages.required,
          }),
        ];
      }
  }

  return schema;
}

function buildResolver(
  fieldDefinitions: z.infer<typeof formSchema>["fields"],
  validationMessages: ValidationMessages
) {
  const entries = fieldDefinitions.flatMap((field) => {
    if (field._type === "fieldset") {
      return field.fields
        .filter((f) => f._type !== "eligibilityNote")
        .map((f) => convertFieldDefinitionToResolver(f, validationMessages));
    }
    if (field._type === "eligibilityNote") return [];
    return [convertFieldDefinitionToResolver(field, validationMessages)];
  });

  // Dynamically build a zod schema based on the field definitions
  return zodResolver(z.object(Object.fromEntries(entries)));
}

export interface FormData {
  [key: string]: string | number | boolean;
}

export type FormBuilderProps = z.infer<typeof formSchema> & {
  className?: string;
  inverse?: boolean;
  theme?: "blue" | "light";
  /**
   * additional properties to be sent to HubSpot
   */
  additionalProperties?: Record<string, string | boolean>;
  /**
   * Name of a submitted field holding a "Full name" value that should be
   * split into `firstname`/`lastname` before submission. For a HubSpot form
   * whose fields have been simplified into one name input, this lets the
   * (still server-rendered) caller fill a `lastname` property the real
   * HubSpot form requires without rendering a second input for it. A
   * function prop can't be used here since callers are Server Components
   * and this is a Client Component.
   */
  deriveLastNameFromField?: string;
  /**
   * Name of a submitted email field whose domain should be extracted into
   * the HubSpot company-domain property (`0-2/domain`) — for a simplified
   * form that doesn't render a separate company-website input.
   */
  deriveDomainFromField?: string;
  enableContactPrefill?: boolean;
};

export function FormBuilder({
  background,
  id,
  name,
  guid,
  fields,
  inverse,
  disclaimerText,
  submitButtonText,
  successState,
  className,
  additionalProperties = {},
  adrollSegmentId,
  deriveLastNameFromField,
  deriveDomainFromField,
  enableContactPrefill = false,
}: FormBuilderProps) {
  const [state, setState] = useState<"form" | "pending" | "success" | "error">(
    "form"
  );
  const hubspotContext = useHubspotContext();
  const posthog = usePostHog();
  const locale = useLocale();
  const t = useTranslations("Form");

  // Validation messages from next-intl
  const validationMessages: ValidationMessages = useMemo(
    () => ({
      required: t("required"),
      invalidEmail: t("invalidEmail"),
      invalidNumber: t("invalidNumber"),
      invalidPhone: t("invalidPhone"),
    }),
    [t]
  );

  // Randomized honeypot field name based on a list of options to confuse simple bots
  const honeypotName = useMemo(() => {
    const baseOptions = ["website", "url", "phone", "nickname"];
    const base = baseOptions[Math.floor(Math.random() * baseOptions.length)];
    const suffix = Math.random().toString(36).slice(2, 10);
    return `${base}_${suffix}`;
  }, []);

  const resolver = useMemo(
    () =>
      buildResolver(
        [
          ...fields,
          {
            _type: "textField",
            name: honeypotName,
            type: "text",
            required: false,
            label: honeypotName,
          },
        ],
        validationMessages
      ),
    [fields, honeypotName, validationMessages]
  );

  const form = useForm({ resolver });
  const country = form.watch(COUNTRY_FIELD_NAME);
  const { handleSubmit } = form;

  // Optional eligibility gate (see eligibility-gate.ts): drives the submit
  // label here, shows its note via <EligibilityNote>, and retags the lead on
  // submit. `watch` re-renders this component only when the watched field
  // changes.
  const gate = useMemo(() => findEligibilityGate(fields), [fields]);
  const gateIneligible = gate
    ? isIneligible(gate, form.watch(gate.watch))
    : false;

  useEffect(() => {
    if (!enableContactPrefill) return;

    const applyPrefill = (prefill: Record<string, string>) => {
      form.reset({ ...form.getValues(), ...prefill });
    };
    const pending = readContactPrefill();
    if (pending) applyPrefill(pending);
    return subscribeContactPrefill(applyPrefill);
  }, [enableContactPrefill, form]);

  const onSubmit = handleSubmit(async (data: FormData) => {
    setState("pending");
    if (typeof data[honeypotName] === "string" && data[honeypotName] !== "") {
      await new Promise((resolve) =>
        setTimeout(() => {
          setState("success");
          resolve(0);
        }, 500)
      );
      logger.warn(
        { data },
        "Honeypot filled; dropping submission with generic success"
      );
      return;
    }

    const formData: FormData = {
      id,
      ...additionalProperties,
      ...applySubmitValues(fields, data),
      __hp_name: honeypotName,
    };

    if (deriveLastNameFromField) {
      const fullName = formData[deriveLastNameFromField];
      if (typeof fullName === "string") {
        const trimmed = fullName.trim();
        const spaceIndex = trimmed.indexOf(" ");
        formData[deriveLastNameFromField] =
          spaceIndex === -1 ? trimmed : trimmed.slice(0, spaceIndex);
        formData["lastname"] =
          spaceIndex === -1 ? trimmed : trimmed.slice(spaceIndex + 1).trim();
      }
    }
    if (deriveDomainFromField) {
      const email = formData[deriveDomainFromField];
      if (typeof email === "string") {
        const at = email.indexOf("@");
        formData["0-2/domain"] = at === -1 ? "" : email.slice(at + 1).trim();
      }
    }
    const urlClickIds = getAdClickIdsFromUrl();
    try {
      await submitFormToHubspot(guid, name, formData, hubspotContext, {
        "X-POSTHOG-SESSION-ID": posthog.get_session_id(),
        // @ts-expect-error - sessionManager is private but posthog said we can use it
        "X-POSTHOG-WINDOW-ID": posthog.sessionManager?._windowId as string,
        "X-POSTHOG-DISTINCT-ID": posthog.get_distinct_id(),
        "X-POSTHOG-GCLID":
          urlClickIds.gclid ??
          (posthog.get_property?.("gclid") as string) ??
          "",
        "X-POSTHOG-GBRAID":
          urlClickIds.gbraid ??
          (posthog.get_property?.("gbraid") as string) ??
          "",
        "X-POSTHOG-WBRAID":
          urlClickIds.wbraid ??
          (posthog.get_property?.("wbraid") as string) ??
          "",
        "X-POSTHOG-FBCLID":
          urlClickIds.fbclid ??
          (posthog.get_property?.("fbclid") as string) ??
          "",
        "X-POSTHOG-RDT-CID":
          urlClickIds.rdt_cid ??
          (posthog.get_property?.("rdt_cid") as string) ??
          "",
        "X-POSTHOG-LI-FAT-ID":
          urlClickIds.li_fat_id ??
          (posthog.get_property?.("li_fat_id") as string) ??
          "",
      });
      setState("success");
      if (data["email"]) {
        posthog.identify(
          data["email"] as string,
          // storing hubspot as contact/company properties as well in posthog
          snakeCaseObjectKeys(formData)
        );
      }

      if (adrollSegmentId) {
        try {
          window.__adroll?.record_user({
            adroll_segments: adrollSegmentId,
          });
        } catch (error) {
          logger.error(
            { error, adrollSegmentId },
            "Failed to track AdRoll segment"
          );
        }
      }
    } catch (error: unknown) {
      setState("error");
      logger.error({ error }, "Error handling form submission");
      posthog.captureException(error);
    }
  });

  const darkOrInverse = background === "dark" || inverse;

  // Same treatment ExternalLink gives app.wayflyer.com links: carry the ad
  // click IDs for conversion attribution, and create the PostHog profile when
  // the visitor has consented.
  const redirectToApp = (target: string) => {
    let href = target;
    try {
      const clickIds = resolveAppLinkAdClickIds(getAdClickIdsFromUrl(), (key) =>
        posthog.get_property?.(key)
      );
      if (Object.keys(clickIds).length > 0) {
        href = appendAppLinkAdClickIdsToUrl(target, clickIds);
      }
      if (posthog.__loaded && posthog.has_opted_in_capturing()) {
        posthog.createPersonProfile();
      }
    } catch (error) {
      logger.error({ error }, "Error preparing ineligible redirect");
    }
    window.location.assign(href);
  };

  const handleTryAgain = () => {
    form.reset();
    setState("form");
  };

  const FieldFactory = useCallback(
    (fieldDefinition: FieldDefinitionTypes) => {
      switch (fieldDefinition._type) {
        case "textField":
          return (
            <FormField
              key={fieldDefinition.name}
              name={fieldDefinition.name}
              defaultValue=""
              render={({ field, fieldState }) => (
                <FormItem className={cn("field flex w-full flex-col gap-y-4")}>
                  <FormLabel
                    className={cn("text-body-sm font-normal text-black", {
                      "text-text-on-dark": darkOrInverse,
                      "sr-only": fieldDefinition.hideLabel,
                    })}
                  >
                    {fieldDefinition.label}
                    {fieldDefinition.required && "*"}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type={
                        fieldDefinition.type === "number"
                          ? "text"
                          : fieldDefinition.type
                      }
                      inputMode={
                        fieldDefinition.type === "number"
                          ? "numeric"
                          : undefined
                      }
                      aria-invalid={Boolean(fieldState.error)}
                      placeholder={fieldDefinition.placeholder ?? undefined}
                      autoComplete={getAutocomplete(
                        fieldDefinition.name,
                        fieldDefinition.type
                      )}
                      suppressHydrationWarning
                      {...field}
                    />
                  </FormControl>
                  <FormMessage inverse={darkOrInverse} />
                </FormItem>
              )}
            />
          );
        case "dropdown":
          return (
            <FormField
              key={fieldDefinition.name}
              name={fieldDefinition.name}
              defaultValue=""
              render={({ field, fieldState }) => {
                const selectItems = fieldDefinition.options.map((option) => {
                  const usdValue = option.value ?? option.label;
                  const isRevenue = fieldDefinition.name === REVENUE_FIELD_NAME;
                  const displayLabel = isRevenue
                    ? localizeRevenueLabel(option.label, country, locale)
                    : option.label;
                  return { value: usdValue, label: displayLabel };
                });

                return (
                  <FormItem
                    key={fieldDefinition.name}
                    className="flex w-full flex-col gap-y-4"
                  >
                    <FormLabel
                      className={cn("text-body-sm font-normal text-black", {
                        "text-text-on-dark": darkOrInverse,
                        "sr-only": fieldDefinition.hideLabel,
                      })}
                    >
                      {fieldDefinition.label}
                      {fieldDefinition.required && "*"}
                    </FormLabel>
                    <Select
                      items={selectItems}
                      onValueChange={field.onChange}
                      value={(field.value as string) ?? ""}
                    >
                      <FormControl>
                        <SelectTrigger aria-invalid={Boolean(fieldState.error)}>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>

                      <SelectContent>
                        {fieldDefinition.optionGroups?.length
                          ? fieldDefinition.optionGroups.map((group, i) => (
                              <Fragment key={group.label}>
                                {i > 0 ? (
                                  <SelectSeparator className="my-1" />
                                ) : null}
                                <SelectGroup>
                                  <SelectLabel
                                    className={selectGroupLabelClassName}
                                  >
                                    {group.label}
                                  </SelectLabel>
                                  {group.options.map((option) => {
                                    const optionValue =
                                      option.value ?? option.label;
                                    return (
                                      <SelectItem
                                        key={optionValue}
                                        value={optionValue}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    );
                                  })}
                                </SelectGroup>
                              </Fragment>
                            ))
                          : selectItems.map(({ value: usdValue, label }) => (
                              <SelectItem key={usdValue} value={usdValue}>
                                {label}
                              </SelectItem>
                            ))}
                      </SelectContent>
                    </Select>
                    <FormMessage inverse={darkOrInverse} />
                  </FormItem>
                );
              }}
            />
          );
        case "eligibilityNote":
          return <EligibilityNote gate={fieldDefinition} />;
        case "checkbox":
          return (
            <FormField
              key={fieldDefinition.name}
              name={fieldDefinition.name}
              defaultValue={Boolean(fieldDefinition.defaultValue)}
              render={({ field }) => (
                <FormItem className="field flex w-full flex-row items-center gap-x-3">
                  <FormControl>
                    <Checkbox
                      suppressHydrationWarning
                      className="cursor-pointer"
                      checked={Boolean(field.value)}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel
                    className={cn(
                      "text-body-sm cursor-pointer font-normal text-black",
                      {
                        "text-text-on-dark": darkOrInverse,
                        "sr-only": fieldDefinition.hideLabel,
                      }
                    )}
                  >
                    {fieldDefinition.label}
                    {fieldDefinition.required && "*"}
                  </FormLabel>
                </FormItem>
              )}
            />
          );
      }
    },
    [darkOrInverse, country, locale]
  );

  return (
    <Form {...form}>
      {enableContactPrefill ? <ContactPrefillTool fields={fields} /> : null}
      <form
        id={id}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          // An ineligible value skips validation and HubSpot entirely: the
          // visitor goes to the standard apply page, so they are never
          // recorded as a promotion lead.
          if (gate && isIneligible(gate, form.getValues(gate.watch))) {
            const target = buildIneligibleRedirectUrl(
              gate,
              window.location.search
            );
            if (target) {
              redirectToApp(target);
              return;
            }
          }
          void onSubmit();
        }}
        className={className}
      >
        {/* Honeypot field: hidden from users, visible to naive bots */}
        <FormField
          key={honeypotName}
          name={honeypotName}
          defaultValue=""
          render={({ field }) => (
            <input
              suppressHydrationWarning
              type="text"
              {...field}
              autoComplete="off"
              tabIndex={-1}
              className="hidden"
              aria-hidden="true"
            />
          )}
        />
        {state === "pending" && (
          <div
            role="status"
            aria-live="polite"
            aria-label={t("submitting")}
            style={{
              height: `${calculateFormHeight(fields.length)}px`,
            }}
            className="flex w-full items-center justify-center"
          >
            <Spinner inverse={darkOrInverse} />
          </div>
        )}
        {state === "form" && (
          <div
            className={cn("w-full", {
              "flex gap-x-2": fields.length === 1,
              "flex flex-col gap-y-5 md:gap-y-6 xl:gap-y-8": fields.length > 1,
            })}
          >
            <div
              className={cn(
                "flex flex-1 flex-col [&:has(>.field)]:gap-y-6 lg:[&:has(>.field)]:gap-y-4 [&:has(>fieldset)]:gap-y-6 lg:[&:has(>fieldset)]:gap-y-8"
              )}
            >
              {fields.map((field, fieldIndex) => {
                if (field._type === "fieldset") {
                  return (
                    <fieldset
                      key={`${field.legend}-${fieldIndex}`}
                      className="text-body-md"
                    >
                      <legend
                        className={cn(labelVariants(), {
                          "sr-only": field.hideLegend,
                          "mb-5": !field.hideLegend,
                        })}
                      >
                        {field.legend}
                      </legend>
                      <div
                        className={cn({
                          "grid gap-y-6 lg:grid-cols-2 lg:gap-x-8 lg:gap-y-4":
                            field.layout === "grid",
                          "flex flex-col gap-y-4": field.layout === "stack",
                        })}
                      >
                        {field.fields.map((field) => (
                          <FieldFactory key={field.name} {...field} />
                        ))}
                      </div>
                    </fieldset>
                  );
                }

                return <FieldFactory key={field.name} {...field} />;
              })}
            </div>
            <div className="flex flex-col gap-y-8">
              <Button
                className="h-[38px] w-full sm:w-auto sm:self-start"
                variant="default"
                type="submit"
                size="default"
              >
                {gateIneligible && gate?.ineligibleSubmitLabel
                  ? gate.ineligibleSubmitLabel
                  : submitButtonText}
              </Button>
              {disclaimerText && <DisclaimerRichText value={disclaimerText} />}
            </div>
          </div>
        )}
        {state === "success" && (
          <div
            role="status"
            aria-live="polite"
            className={cn("rounded-container flex items-baseline gap-x-2", {
              "bg-surface p-3": darkOrInverse,
            })}
          >
            <span className="bg-icon-success flex size-5 shrink-0 items-center justify-center rounded-full">
              <Check className="stroke-icon-on-dark size-3 stroke-[3px]" />
            </span>
            {(successState.title ||
              successState.description ||
              successState.cta) && (
              <div>
                {successState.title && (
                  <h3 className="text-heading-18 font-semibold text-black">
                    {successState.title}
                  </h3>
                )}
                {successState.description && (
                  <div className="text-body text-grey-600">
                    {/* A bare <p> has no margin under preflight. text-body is
                        1rem/1.5, so space-y-6 (24px) is exactly one blank
                        line between the paragraphs. */}
                    <MarkdownText className="space-y-6">
                      {successState.description ?? ""}
                    </MarkdownText>
                  </div>
                )}
                {successState.cta && (
                  <Button
                    size="lg"
                    className="mt-4 w-full sm:w-auto"
                    // external routes through ExternalLink, which appends ad
                    // click IDs and creates the PostHog person profile for
                    // app.wayflyer.com links. Same tab either way.
                    render={
                      <Link
                        href={successState.cta.href}
                        external={successState.cta.external}
                      />
                    }
                  >
                    {successState.cta.label}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
        {state === "error" && (
          <div
            role="alert"
            aria-live="assertive"
            className="flex flex-col gap-y-6"
          >
            <div
              className={cn("rounded-container flex flex-col gap-y-4 p-6", {
                "bg-surface": darkOrInverse,
                "bg-grey-50": !darkOrInverse,
              })}
            >
              <h3
                className={cn("text-heading-18 font-semibold", {
                  "text-text-on-dark": darkOrInverse,
                  "text-black": !darkOrInverse,
                })}
              >
                Sorry, we&apos;ve encountered an error
              </h3>
              <TypographyP
                className={cn("text-body-sm", {
                  "text-text-on-dark": darkOrInverse,
                  "text-grey-800": !darkOrInverse,
                })}
              >
                We are currently experiencing an internal server error.
                We&apos;re working hard to get this fixed. Please try again
                later. For more information{" "}
                <a
                  href="mailto:support@wayflyer.com"
                  className={cn("underline hover:no-underline", {
                    "text-text-on-dark": darkOrInverse,
                    "text-pulse-100": !darkOrInverse,
                  })}
                >
                  contact our support team
                </a>
                .
              </TypographyP>
            </div>
            <Button
              className="w-full sm:w-auto sm:self-start"
              variant="default"
              type="button"
              size="sm"
              onClick={handleTryAgain}
            >
              Try again
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
}

function DisclaimerRichText({ value }: { value: string }) {
  return (
    <div className="text-body-sm text-black">
      <MarkdownText>{value ?? ""}</MarkdownText>
    </div>
  );
}
